import React, { useEffect, useRef, useState } from 'react'
import { Checkbox, Input } from 'antd'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import { Modal } from '@/components/dialog/modal'
import { shopStore } from '@/store/shop'
import $msg from '@/utils/message'
import { ImportStepBar } from '../member-import/import-layout'
import MemberNotice from '../member-notice'
import {
    exportShopMemberships,
    getShopAuthenticationInfo,
    getShopMembershipExportCount,
    getShopMembershipsSheetUrl,
    getVerificationCode,
    verifyShopMembershipExportAdminPhone,
    verifyShopMembershipExportLegalRepresentativeId,
} from '../api'
import { shouldRequestStoredSheetUrl } from './member-export-utils'

interface Props {
    visible: boolean
    onCancel: () => void
}

const maskPhone = (phone?: string) => (phone ? phone.replace(/^(\d{3}).*(\d{4})$/, '$1****$2') : '-')
const exportSteps = [{ title: '超管验证' }, { title: '法人验证' }, { title: '导出文件' }]

const MemberExport: React.FC<Props> = props => {
    const [sheetUrl, setSheetUrl] = useState('')
    const [exporting, setExporting] = useState(false)
    const [agreed, setAgreed] = useState(false)
    const [step, setStep] = useState(0)
    const [verifyCode, setVerifyCode] = useState('')
    const [idTail, setIdTail] = useState('')
    const [countdown, setCountdown] = useState(0)
    const [legalRepresentativeName, setLegalRepresentativeName] = useState('')
    const [legalVerified, setLegalVerified] = useState(false)
    const pollCount = useRef(0)
    const countReq = useRequest(getShopMembershipExportCount, {
        refreshDeps: [props.visible],
        ready: props.visible,
        onError: e => $msg('error', e.message || '获取导出次数失败'),
    })
    const refreshCountRef = useRef(countReq.refresh)
    const authReq = useRequest(getShopAuthenticationInfo, {
        refreshDeps: [props.visible],
        ready: props.visible,
        onSuccess: res => setLegalRepresentativeName(res?.legal_representative_name || ''),
        onError: e => $msg('error', e.message || '获取店铺认证信息失败'),
    })
    const codeReq = useRequest(
        () => getVerificationCode({ phone: shopStore.shopInfo?.creator.phone || '', code_type: 3 }),
        {
            manual: true,
            onSuccess: () => {
                setCountdown(60)
                $msg('success', '验证码已发送')
            },
            onError: e => $msg('error', e.message || '发送验证码失败'),
        },
    )
    const phoneVerifyReq = useRequest(
        () =>
            verifyShopMembershipExportAdminPhone({ phone: shopStore.shopInfo?.creator.phone || '', code: verifyCode }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '超管验证通过')
                setStep(1)
            },
            onError: e => $msg('error', e.message || '超管验证失败'),
        },
    )
    const legalVerifyReq = useRequest(() => verifyShopMembershipExportLegalRepresentativeId({ id_tail: idTail }), {
        manual: true,
        onSuccess: () => {
            setLegalVerified(true)
            $msg('success', '法人验证通过')
        },
        onError: e => $msg('error', e.message || '法人验证失败'),
    })
    const exportReq = useRequest(exportShopMemberships, {
        manual: true,
        onSuccess: () => {
            setSheetUrl('')
            pollCount.current = 0
            setExporting(true)
            $msg('success', '已开始导出')
        },
        onError: e => $msg('error', e.message || '导出失败'),
    })
    const sheetReq = useRequest(getShopMembershipsSheetUrl, {
        manual: true,
        onSuccess: res => {
            if (res.sheet_url) {
                setSheetUrl(res.sheet_url)
                refreshCountRef.current()
            }
        },
        onError: e => $msg('error', e.message || '获取导出文件失败'),
    })

    useEffect(() => {
        if (!props.visible) {
            setExporting(false)
            setSheetUrl('')
            setAgreed(false)
            setStep(0)
            setVerifyCode('')
            setIdTail('')
            setCountdown(0)
            setLegalVerified(false)
            pollCount.current = 0
        }
    }, [props.visible])

    useEffect(() => {
        refreshCountRef.current = countReq.refresh
    }, [countReq.refresh])

    useEffect(() => {
        if (!countdown) return
        const timer = window.setInterval(() => setCountdown(value => Math.max(value - 1, 0)), 1000)

        return () => window.clearInterval(timer)
    }, [countdown])

    useEffect(() => {
        if (!props.visible || !exporting) return
        const timer = window.setInterval(() => {
            getShopMembershipsSheetUrl()
                .then(res => {
                    if (res.sheet_url) {
                        setSheetUrl(res.sheet_url)
                        setExporting(false)
                        refreshCountRef.current()
                        window.clearInterval(timer)
                    }
                })
                .catch(e => {
                    setExporting(false)
                    window.clearInterval(timer)
                    $msg('error', e.message || '获取导出文件失败')
                })
            pollCount.current += 1
            if (pollCount.current >= 120) {
                setExporting(false)
                window.clearInterval(timer)
                $msg('warning', '导出耗时较长，请稍后重新打开会员导出查看文件')
            }
        }, 1000)

        return () => window.clearInterval(timer)
    }, [exporting, props.visible])

    useEffect(() => {
        if (shouldRequestStoredSheetUrl(props.visible, sheetUrl, exporting, legalVerified)) {
            sheetReq.run()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exporting, legalVerified, props.visible, sheetUrl])

    if (!props.visible) return null

    const footer = (
        <div className="member-export__actions">
            <Button
                types="default"
                loading={exportReq.loading || exporting || sheetReq.loading}
                disabled={
                    exporting ||
                    countReq.loading ||
                    sheetReq.loading ||
                    countReq.data?.times_left === 0 ||
                    !legalVerified
                }
                onClick={() => {
                    setStep(2)
                    exportReq.run()
                }}
            >
                {exporting ? '导出中' : '开始导出'}
            </Button>
            {sheetUrl && (
                <Button types="primary" onClick={() => window.open(sheetUrl, '_blank')}>
                    下载文件
                </Button>
            )}
        </div>
    )

    return (
        <Modal title="会员导出" isShow type="success" onClose={props.onCancel} footer={footer} width={640}>
            <div>
                本月已导出 {countReq.data?.consumed_count || 0} 次，剩余 {countReq.data?.times_left || 0} 次。
            </div>
            <MemberNotice className="member-export__notice" type="warning">
                会员数据包含顾客隐私信息，导出前需要完成协议确认、超管手机号验证和法人身份验证。
            </MemberNotice>
            <Checkbox checked={agreed} onChange={e => setAgreed(e.target.checked)}>
                已阅读并同意会员导出协议，不将用户数据用于违法行为，不售卖或泄露给第三方。
            </Checkbox>
            <ImportStepBar steps={exportSteps} current={step} className="member-export__steps" />
            <div className="member-export__shop">
                {[
                    { label: '店铺', value: shopStore.shopInfo?.shop_name || '-' },
                    { label: '店铺号', value: shopStore.shopInfo?.id || '-' },
                    { label: '超管手机号', value: maskPhone(shopStore.shopInfo?.creator.phone) },
                    { label: '法人代表', value: legalRepresentativeName || '-' },
                ].map(item => (
                    <div className="member-export__shop-row" key={item.label}>
                        <span className="member-export__shop-label">{item.label}：</span>
                        <span className="member-export__shop-value">{item.value}</span>
                    </div>
                ))}
            </div>
            {step === 0 ? (
                <div className="member-export__verify-row">
                    <Input
                        value={verifyCode}
                        maxLength={4}
                        placeholder="请输入超管手机号验证码"
                        onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    />
                    <Button
                        types="primary"
                        loading={codeReq.loading}
                        disabled={!agreed || countdown > 0 || !shopStore.shopInfo?.creator.phone}
                        onClick={() => codeReq.run()}
                    >
                        {countdown ? `${countdown}s 后重发` : '发送验证码'}
                    </Button>
                    <Button
                        types="default"
                        loading={phoneVerifyReq.loading}
                        disabled={!agreed || verifyCode.length !== 4}
                        onClick={() => phoneVerifyReq.run()}
                    >
                        验证
                    </Button>
                </div>
            ) : (
                <div className="member-export__verify-row">
                    <Input
                        value={idTail}
                        maxLength={6}
                        placeholder="请输入法人代表身份证后六位"
                        onChange={e => setIdTail(e.target.value.replace(/[^0-9xX]/g, '').slice(0, 6))}
                        disabled={legalVerified}
                    />
                    <Button
                        types="default"
                        loading={legalVerifyReq.loading || authReq.loading}
                        disabled={legalVerified || idTail.length !== 6}
                        onClick={() => legalVerifyReq.run()}
                    >
                        {legalVerified ? '已验证' : '验证'}
                    </Button>
                </div>
            )}
        </Modal>
    )
}

export default MemberExport
